import React, { useCallback, useEffect, useState } from 'react';
import classNames from 'classnames';
import GridView from '@mui/icons-material/GridView';
import { Box, IconButton, Skeleton, Typography } from '@mui/material';
import CloudflareResizedImage from 'components/CloudflareResizedImage';
import Expand from 'components/Icons/Expand';
import type { CamForecastComparison, CamForecastTopMatch } from 'types/camera';

import { CamMatchesForecastModal } from './CamMatchesForecastModal';
import {
  CAM_MATCH_EXPANDED_HERO_HEIGHT,
  CAM_MATCH_EXPANDED_HERO_QUALITY,
  CAM_MATCH_EXPANDED_HERO_WIDTH,
  prefetchCamMatchExpandedHero,
} from './prefetchCamMatchThumbnails';
import styles from './CamMatchesExpanded.module.scss';

export interface CamMatchesExpandedProps {
  spotId: string;
  match: CamForecastTopMatch;
  forecastComparison: Pick<CamForecastComparison, 'datetime' | 'utcOffset'> | null;
  onCollapse: () => void;
}

export const CamMatchesExpanded: React.FC<CamMatchesExpandedProps> = ({
  spotId,
  match,
  forecastComparison,
  onCollapse,
}) => {
  const images = match.multipleThumbnailUrls.filter(Boolean);
  const primaryImage = images[0];
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isHeroImageLoading, setIsHeroImageLoading] = useState(true);

  useEffect(() => {
    if (primaryImage) {
      setIsHeroImageLoading(true);
      prefetchCamMatchExpandedHero(primaryImage);
    }
  }, [primaryImage, match.exemplarId]);

  const handleHeroImageLoaded = useCallback(() => {
    setIsHeroImageLoading(false);
  }, []);

  const handleHeroImageError = useCallback(() => {
    setIsHeroImageLoading(false);
  }, []);

  useEffect(() => {
    if (isModalOpen) {
      return undefined;
    }
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onCollapse();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [isModalOpen, onCollapse]);

  const handleExpandClick = useCallback(() => {
    setIsModalOpen(true);
  }, []);

  if (!images.length) return null;

  const swell = Math.round(match.swellSimilarity);
  const wind = Math.round(match.windSimilarity);
  const tide = Math.round(match.tideSimilarity);
  const overall = Math.round(match.overallSimilarity);

  return (
    <Box className={styles.root} data-testid="cam-matches-expanded">
      <Box className={styles.card}>
        <Box className={styles.hero} aria-busy={isHeroImageLoading}>
          <Box
            className={classNames(styles.heroImageWrap, styles.heroImageWrapClickable)}
            onClick={handleExpandClick}
          >
            {isHeroImageLoading ? (
              <Skeleton
                className={styles.heroImageSkeleton}
                variant="rectangular"
                animation="wave"
                width="100%"
                height="100%"
                aria-hidden
                data-testid="cam-matches-expanded-image-skeleton"
              />
            ) : null}
            <Box
              className={classNames(styles.heroImage, isHeroImageLoading && styles.heroImageHidden)}
              data-testid="cam-matches-expanded-hero-image"
              data-loading={isHeroImageLoading ? 'true' : 'false'}
            >
              <CloudflareResizedImage
                key={primaryImage}
                src={primaryImage}
                alt={
                  images.length > 1
                    ? `Cam match preview (1 of ${images.length} stills)`
                    : 'Cam match preview'
                }
                width={CAM_MATCH_EXPANDED_HERO_WIDTH}
                height={CAM_MATCH_EXPANDED_HERO_HEIGHT}
                sizes={`${CAM_MATCH_EXPANDED_HERO_WIDTH}px`}
                layout="fill"
                loading="eager"
                objectFit="cover"
                objectPosition="center"
                priority
                quality={CAM_MATCH_EXPANDED_HERO_QUALITY}
                cloudflareOptions={{ fit: 'cover' }}
                onLoadingComplete={handleHeroImageLoaded}
                onError={handleHeroImageError}
              />
            </Box>
          </Box>

          <Box aria-hidden className={styles.thumbGradient} />

          <Box className={styles.topOverlay}>
            <Box className={styles.metricsBlock}>
              <Box>
                <Typography component="span" className={styles.metricPct}>
                  {overall}%
                </Typography>
                <Typography component="span" className={styles.metricRest}>
                  {' '}
                  Overall match
                </Typography>
              </Box>
              <Box>
                <Typography component="span" className={styles.metricPct}>
                  {swell}%{' '}
                </Typography>
                <Typography component="span" className={styles.metricRest}>
                  Swell ·{' '}
                </Typography>
                <Typography component="span" className={styles.metricPct}>
                  {wind}%{' '}
                </Typography>
                <Typography component="span" className={styles.metricRest}>
                  Wind ·{' '}
                </Typography>
                <Typography component="span" className={styles.metricPct}>
                  {tide}%{' '}
                </Typography>
                <Typography component="span" className={styles.metricRest}>
                  Tide
                </Typography>
              </Box>
            </Box>
          </Box>

          <IconButton
            type="button"
            size="small"
            className={styles.expandButton}
            aria-label="Open forecast match details"
            aria-expanded={isModalOpen}
            data-testid="cam-matches-expanded-open-modal"
            disableFocusRipple
            disableRipple
            disableTouchRipple
            onClick={handleExpandClick}
          >
            <Box className={styles.expandIconWrap}>
              <Expand fill="#fff" />
            </Box>
          </IconButton>

          <IconButton
            type="button"
            size="small"
            className={styles.gridBackButton}
            aria-label="Return to cam matches grid"
            data-testid="cam-matches-expanded-back"
            disableFocusRipple
            disableRipple
            disableTouchRipple
            onClick={onCollapse}
          >
            <GridView className={styles.gridBackIcon} />
          </IconButton>
        </Box>
      </Box>

      <CamMatchesForecastModal
        open={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        spotId={spotId}
        match={match}
        forecastComparison={forecastComparison}
      />
    </Box>
  );
};
